import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { gray, magenta } from '@carbon/colors';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Button,
  IconButton,
  InlineLoading,
  InlineNotification,
  Slider,
} from '@carbon/react';
import {
  Edit,
  Information,
  Locked,
  Maximize,
  Minimize,
  Unlocked,
} from '@carbon/react/icons';
import type mapboxgl from 'mapbox-gl';
import { Layer, Marker, Source, type MarkerDragEvent } from 'react-map-gl';
import { Map } from 'components/map';
import { FloatingTooltip } from 'components/floating-tooltip';
import { WellMapLayer } from 'components/well-page-content/well-map-layer';
import {
  WellScopedLayers,
  type WellboreSummary,
} from 'components/well-page-content/well-scoped-layers';
import {
  isProducingVisibleAtom,
  sidetrackVisibilityAtom,
} from 'components/well-page-content/atoms';
import { formatMutationResource } from 'utils/misc';
import type {
  WellborePoint,
  WellboreGeometryEditPoint,
} from 'types/api-responses';
import type { ColorByRule } from 'components/color-by-attribute-modal';
import {
  preDrillWellMapAtom,
  preDrillMapConfigAtom,
  preDrillColorByUwiMapAtom,
  preDrillMapRefAtom,
  preDrillEditingLineAtom,
  preDrillSectionLineAtom,
  preDrillSectionMarkersAtom,
  type SectionLine,
} from './atoms';
import { colorForBoreUwi } from './utils';
import {
  centerOf,
  defaultSectionLine,
  DEFAULT_DRAG_INFLUENCE_FT,
  MAX_DRAG_INFLUENCE_FT,
  moveWithFalloff,
  samePoints,
  stickWorkingPoints,
  toWorking,
  type Position,
  type WorkingPoint,
} from './workspace-map-utils';
import { pointAtDistance } from 'components/cross-section/section-geometry';
import { PreDrillScopedWellsLayer } from './pre-drill-scoped-wells-layer';
import styles from './workspace-map.module.scss';

/** Story 7's editable geometry, given where editing belongs (the geometry tearsheet).
 *  Before the backend serves derived points, the map falls back to the filed stick. */
export interface WorkspaceMapGeometry {
  points: WellborePoint[];
  landingPointIndex: number | null;
  /** Whether the workspace may be edited (a promoted one is read-only). */
  editable: boolean;
  /** A geometry edit is in flight; the map holds its working copy meanwhile. */
  committing: boolean;
  /** The bore carries a vendor survey, so arming edit raises the confidence warning. */
  surveyed: boolean;
  /** False while the points are still loading. Until then `points` is empty because nothing
   *  has arrived, not because the bore has none, so the map does not call the bore a stick. */
  pointsLoaded?: boolean;
  /** The points read failed: the map says so rather than reading the bore as a stick. */
  pointsFailed?: boolean;
  /** False until the backend serves the geometry write; Done Editing then says so. */
  canCommit: boolean;
  /** Done Editing with a change: the whole list in order, and where the landing point sits. */
  onCommit: (
    points: WellboreGeometryEditPoint[],
    landingPointIndex: number | null
  ) => void;
}

/** What the geometry tearsheet's Save and Cancel act on. */
export interface WorkspaceMapHandle {
  /** An armed edit that differs from what is committed. */
  hasPendingEdit: () => boolean;
  /** Leave edit mode, keeping the points that were just sent. */
  endEditing: () => void;
  /** Commit it, exactly as Done Editing would. */
  commitPending: () => void;
  /** Drop it and leave edit mode. */
  discardPending: () => void;
}

interface WorkspaceMapProps {
  uwi14: string;
  /** All color rules; the map paints each bore via the enabled, map-targeting
   *  rules (Well Qualifier by subject/nearby, others by the resolved UWI map). */
  colorRules: ColorByRule[];
  geometry?: WorkspaceMapGeometry;
  /** Reports whether an uncommitted edit is on the map. */
  onPendingEditChange?: (pending: boolean) => void;
  /** Reports entering and leaving edit mode, so the host can hold its Save meanwhile. */
  onEditingChange?: (editing: boolean) => void;
  /** Reports expand / shrink, so a host laying the map beside other views can give an
   *  expanded map the whole row (INS-12936). */
  onExpandedChange?: (expanded: boolean) => void;
}

const DEFAULT_ZOOM = 12;
// The subject bore renders black (matches the gun barrel). mapbox paint takes literal
// colours, so these come from the Carbon palette rather than a CSS token.
const SUBJECT_WELL_COLOR = gray[100];
const WORKING_LINE_COLOR = SUBJECT_WELL_COLOR;
const WORKING_LINE_WIDTH = 5;
// An invisible, fat line over the bore so a click near it still lands on it.
const WORKING_HITBOX_WIDTH = 22;
const SECTION_LINE_COLOR = magenta[50];
const SECTION_LINE_WIDTH = 2.5;
// How close a double-click must land to count as hitting a vertex.
const VERTEX_HIT_RADIUS_PX = 12;
// The transparent click target over the bore.
const WORKING_HITBOX_LAYER_ID = 'pre-drill-working-geometry-hitbox';

/**
 * Workspace Map: the Well Page map with the subject permit and nearby wellbores. Given
 * a geometry (Story 7) it also edits the bore's points in place and carries the A–A′
 * section line, which is view state and never saved.
 * @param {WorkspaceMapProps} props The subject UWI, the colour rules and the geometry.
 * @return {JSX.Element} The map section.
 */
export const WorkspaceMap = forwardRef<WorkspaceMapHandle, WorkspaceMapProps>(
  (
    {
      uwi14,
      colorRules,
      geometry,
      onPendingEditChange,
      onEditingChange,
      onExpandedChange,
    },
    ref
  ) => {
    const [wellMapResource, fetchWellMap] = useAtom(preDrillWellMapAtom);
    const isProducingVisible = useAtomValue(isProducingVisibleAtom);
    const sidetrackVisibility = useAtomValue(sidetrackVisibilityAtom);
    const colorByUwiMap = useAtomValue(preDrillColorByUwiMapAtom);
    const mapRef = useAtomValue(preDrillMapRefAtom);

    // Story 7 edit mode: a working copy of the points, committed as one list on Done
    // Editing. The two ends open locked and unlock explicitly.
    const [armed, setArmed] = useState(false);
    const [unlocked, setUnlocked] = useState(false);
    // How far a drag pulls the points around it, in feet along the bore.
    const [influenceFt, setInfluenceFt] = useState(DEFAULT_DRAG_INFLUENCE_FT);
    // Expanded map (UAT 19): fills the sheet; the height is the stylesheet's.
    const [expanded, setExpanded] = useState(false);
    const [working, setWorking] = useState<WorkingPoint[]>([]);
    // mapbox handlers are bound once, so they read the points through a ref.
    const workingRef = useRef<WorkingPoint[]>([]);
    workingRef.current = working;
    const [sectionLine, setSectionLine] = useState<SectionLine | null>(null);
    // Done Editing was pressed on a change the backend cannot yet accept.
    const [saveUnavailable, setSaveUnavailable] = useState(false);
    // The subject bore as the wells query returns it, read early so the points the map
    // edits can fall back to its filed stick.
    const subjectBore =
      formatMutationResource(wellMapResource).data?.results?.[0]
        ?.wellBores?.[0];
    // What the map edits: the backend's derived points when it serves them, otherwise
    // the filed stick, so the geometry is editable before that endpoint exists.
    const committed = useMemo(() => {
      if (!geometry) return [];
      if (geometry.points.length > 0) return toWorking(geometry.points);
      return stickWorkingPoints(subjectBore);
    }, [geometry, subjectBore]);
    // Follow the committed points when idle; a commit in flight keeps the working copy
    // so the edit doesn't snap back. An armed edit follows only when the set's content
    // changes (a panel write), so a same-set re-read keeps a drag in progress.
    const committing = geometry?.committing ?? false;
    const committedRef = useRef(committed);
    useEffect(() => {
      const changed = !samePoints(committed, committedRef.current);
      committedRef.current = committed;
      if (changed || (!armed && !committing)) setWorking(committed);
    }, [committed, armed, committing]);
    // The section line opens perpendicular to the committed bore and then belongs to
    // the reader; it is view state and never written with the geometry.
    useEffect(() => {
      setSectionLine((current) => current ?? defaultSectionLine(committed));
    }, [committed]);

    const shown = armed || committing ? working : committed;

    const handleArm = () => {
      setWorking(committed);
      setUnlocked(false);
      setArmed(true);
    };
    const commit = () => {
      if (!geometry) return;
      const landingPointIndex = working.findIndex(
        (point) => point.type === 'lp'
      );
      const changed = !samePoints(working, committed);
      if (changed && !geometry.canCommit) {
        // Keep the edit on screen: losing it silently would be worse than saying why.
        setSaveUnavailable(true);
        return;
      }
      setSaveUnavailable(false);
      // Nothing moved: there is nothing to write, and Done means done.
      if (!changed) {
        setArmed(false);
        return;
      }
      geometry.onCommit(
        working.map((point) => ({
          // Named, the backend moves that row and keeps its depth and provenance;
          // unnamed, it is a point this edit added and takes one between its
          // neighbours.
          ...(point.id === undefined ? {} : { id: point.id }),
          latitude: point.lat,
          longitude: point.lon,
          point_type: point.type,
          // A released position stays released through the save.
          location_locked: unlocked ? false : point.locationLocked,
          tvd_ft: point.tvdFt,
        })),
        landingPointIndex === -1 ? null : landingPointIndex
      );
    };
    // Save and Cancel in the sheet's header reach the same state the map's own
    // controls do, so an edit cannot be left behind by closing from the header.
    const pending = armed && !samePoints(working, committed);
    useEffect(() => {
      onPendingEditChange?.(pending);
    }, [pending, onPendingEditChange]);
    useEffect(() => {
      onEditingChange?.(armed);
    }, [armed, onEditingChange]);
    // The cross-section beside the map follows the line as it is dragged.
    const publishSectionLine = useSetAtom(preDrillSectionLineAtom);
    useEffect(() => {
      publishSectionLine(sectionLine);
    }, [sectionLine, publishSectionLine]);
    // And the working line while an edit is armed, so the section can preview where
    // the edit crosses it (UAT 35); nothing once editing ends or the map goes.
    const sectionMarkers = useAtomValue(preDrillSectionMarkersAtom);
    const publishEditingLine = useSetAtom(preDrillEditingLineAtom);
    useEffect(() => {
      publishEditingLine(
        armed
          ? working.map((point) => ({
              lon: point.lon,
              lat: point.lat,
              tvdFt: point.readTvdFt ?? null,
              tvdssFt: point.readTvdssFt ?? null,
            }))
          : null
      );
    }, [armed, working, publishEditingLine]);
    useEffect(() => () => publishEditingLine(null), [publishEditingLine]);
    useImperativeHandle(
      ref,
      () => ({
        hasPendingEdit: () => pending,
        commitPending: () => commit(),
        // After a save the edit is already on its way, so editing ends without the
        // working set being rolled back to what it was before it.
        endEditing: () => {
          setArmed(false);
          setSaveUnavailable(false);
        },
        discardPending: () => {
          setWorking(committed);
          setArmed(false);
          setSaveUnavailable(false);
        },
      }),
      // `commit` is rebuilt every render, so the handle keys on everything it closes
      // over instead (the unlock included); the setters are stable.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [pending, committed, working, geometry, unlocked]
    );

    const handleDrag = (index: number, event: MarkerDragEvent) => {
      setWorking((current) => {
        const point = current[index];
        if (!point) return current;
        return moveWithFalloff(
          current,
          index,
          event.lngLat.lng - point.lon,
          event.lngLat.lat - point.lat,
          influenceFt
        );
      });
    };
    // Insert a vertex where the bore was clicked: the closest position on the nearest
    // segment, unassigned until the points table names it the landing point.
    const handleInsertAt = (screenPoint: { x: number; y: number }) => {
      const map = mapRef?.getMap();
      if (!map) return;
      const points = workingRef.current;
      if (points.length < 2) return;
      let best = {
        index: -1,
        distance: Number.POSITIVE_INFINITY,
        lon: 0,
        lat: 0,
      };
      for (let i = 0; i < points.length - 1; i += 1) {
        const start = map.project([points[i].lon, points[i].lat]);
        const end = map.project([points[i + 1].lon, points[i + 1].lat]);
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const lengthSquared = dx * dx + dy * dy;
        const along =
          lengthSquared === 0
            ? 0
            : ((screenPoint.x - start.x) * dx +
                (screenPoint.y - start.y) * dy) /
              lengthSquared;
        const progress = Math.max(0, Math.min(1, along));
        const x = start.x + dx * progress;
        const y = start.y + dy * progress;
        const distance = Math.hypot(screenPoint.x - x, screenPoint.y - y);
        if (distance < best.distance) {
          const position = map.unproject([x, y]);
          best = { index: i, distance, lon: position.lng, lat: position.lat };
        }
      }
      if (best.index === -1) return;
      setWorking((current) => [
        ...current.slice(0, best.index + 1),
        {
          lon: best.lon,
          lat: best.lat,
          type: 'unassigned' as const,
          locationLocked: false,
          tvdFt: null,
          added: true,
          readTvdFt: null,
          readTvdssFt: null,
        },
        ...current.slice(best.index + 1),
      ]);
    };
    // The vertex under a screen point, within the handle's hit radius; -1 when the
    // point is on open line or map. `interiorOnly` leaves the two ends out, for the
    // delete that never removes them.
    const vertexAt = (
      screenPoint: { x: number; y: number },
      interiorOnly: boolean
    ): number => {
      const map = mapRef?.getMap();
      if (!map) return -1;
      const points = workingRef.current;
      let target = -1;
      let closest = VERTEX_HIT_RADIUS_PX;
      points.forEach((point, index) => {
        if (interiorOnly && (index === 0 || index === points.length - 1))
          return;
        const projected = map.project([point.lon, point.lat]);
        const distance = Math.hypot(
          screenPoint.x - projected.x,
          screenPoint.y - projected.y
        );
        if (distance < closest) {
          closest = distance;
          target = index;
        }
      });
      return target;
    };
    // Double-click a vertex to remove it. The two ends define the bore and stay.
    const handleDeleteAt = (screenPoint: { x: number; y: number }) => {
      const target = vertexAt(screenPoint, true);
      if (target === -1) return false;
      setWorking((current) => current.filter((_point, at) => at !== target));
      return true;
    };
    const handleSectionDrag = (end: 'a' | 'b', event: MarkerDragEvent) => {
      setSectionLine((current) => {
        if (!current) return current;
        return { ...current, [end]: [event.lngLat.lng, event.lngLat.lat] };
      });
    };

    const insertRef = useRef(handleInsertAt);
    insertRef.current = handleInsertAt;
    const deleteRef = useRef(handleDeleteAt);
    deleteRef.current = handleDeleteAt;
    const vertexAtRef = useRef(vertexAt);
    vertexAtRef.current = vertexAt;
    useEffect(() => {
      const map = mapRef?.getMap();
      if (!map || !armed) return undefined;
      const onClick = (event: mapboxgl.MapMouseEvent) => {
        // A click on a vertex belongs to that vertex: its handle sits on the line,
        // so the hitbox alone cannot tell it from the open line beside it, and a
        // double-click to delete arrives as two clicks first. Only open line inserts.
        if (vertexAtRef.current(event.point, false) !== -1) return;
        const features = map.queryRenderedFeatures(event.point, {
          layers: [WORKING_HITBOX_LAYER_ID],
        });
        if (features.length === 0) return;
        insertRef.current(event.point);
      };
      const onDoubleClick = (event: mapboxgl.MapMouseEvent) => {
        // Removing a vertex must not also zoom the map.
        if (deleteRef.current(event.point)) event.preventDefault();
      };
      // The cursor says what a click would do (UAT 31): copy over the open line, where a
      // click inserts; elsewhere the map keeps its grab. Grips carry their own cursors.
      const canvas = map.getCanvas();
      const onMouseMove = (event: mapboxgl.MapMouseEvent) => {
        const overLine =
          vertexAtRef.current(event.point, false) === -1 &&
          map.queryRenderedFeatures(event.point, {
            layers: [WORKING_HITBOX_LAYER_ID],
          }).length > 0;
        canvas.style.cursor = overLine ? 'copy' : '';
      };
      map.on('click', onClick);
      map.on('dblclick', onDoubleClick);
      map.on('mousemove', onMouseMove);
      return () => {
        map.off('click', onClick);
        map.off('dblclick', onDoubleClick);
        map.off('mousemove', onMouseMove);
        canvas.style.cursor = '';
      };
    }, [mapRef, armed]);

    // mapbox-gl@2 only tracks window resize, not its container (mapbox-gl-js#9083),
    // so collapsing the settings panel widens the container without redrawing the
    // canvas. Resize on container change to fill the reclaimed space.
    useEffect(() => {
      const map = mapRef?.getMap();
      const container = map?.getContainer();
      if (!map || !container) return undefined;
      const observer = new ResizeObserver(() => map.resize());
      observer.observe(container);
      return () => observer.disconnect();
    }, [mapRef]);

    useEffect(() => {
      fetchWellMap({
        type: 'POST',
        url: '/queries/wells',
        // /queries/wells keys on the 10-digit UWI (the first 10 of uwi14).
        data: { uwi: uwi14.slice(0, 10), includeDeviationSurvey2d: true },
      });
    }, [uwi14, fetchWellMap]);

    const { loading, data, error } = formatMutationResource(wellMapResource);

    // `error` is a real failure only when it has a type; initial/in-flight states
    // carry an empty-type object, so show the error only on a settled failure.
    if (!loading && error?.type) {
      return (
        <InlineNotification
          kind="error"
          title="Failed to load map"
          lowContrast
        />
      );
    }

    if (!data) {
      return <InlineLoading description="Loading map…" />;
    }

    const wellbores = data.results?.flatMap((result) => result.wellBores) ?? [];
    const center =
      centerOf(wellbores[0]) ??
      (committed[0]
        ? ([committed[0].lon, committed[0].lat] as Position)
        : null);

    const producingWellbores = wellbores.filter(
      (wb) => wb.type === 'producing'
    );
    const sidetrackWellbores = wellbores.filter(
      (wb) => wb.type === 'sidetrack'
    );
    const wellboreSummary: WellboreSummary = {
      producing: producingWellbores.length,
      sidetracks: sidetrackWellbores.map((wb) => wb.uwi14),
    };

    // Color-by: each bore's color comes from the enabled, map-targeting rules
    // (Well Qualifier by subject/nearby, others by the resolved UWI map).
    const colorFor = (boreUwi: string): string | undefined =>
      colorForBoreUwi(boreUwi, uwi14, colorRules, colorByUwiMap);

    if (!center) {
      return (
        <InlineNotification
          kind="info"
          title="No geometry to display on the map"
          lowContrast
        />
      );
    }

    // Only once the points have loaded: before then the map falls back to the filed
    // two-point stick, which would flash the banner on a bore that has more points.
    const stick =
      geometry !== undefined &&
      geometry.pointsLoaded !== false &&
      !geometry.pointsFailed &&
      committed.length === 2;
    const workingLine = {
      type: 'Feature' as const,
      properties: {},
      geometry: {
        type: 'LineString' as const,
        coordinates: shown.map((point) => [point.lon, point.lat]),
      },
    };
    const sectionFeature = sectionLine && {
      type: 'Feature' as const,
      properties: {},
      geometry: {
        type: 'LineString' as const,
        coordinates: [sectionLine.a, sectionLine.b],
      },
    };
    // Where the section is pointing, on the line it was cut along (UAT 36): the
    // distances it reports are measured from A, so the same walk that placed the
    // preview crossing places these.
    const markerAt = (distanceFt: number | null) => {
      if (!sectionLine || distanceFt === null) {
        return null;
      }
      return pointAtDistance(
        [
          { lon: sectionLine.a[0], lat: sectionLine.a[1] },
          { lon: sectionLine.b[0], lat: sectionLine.b[1] },
        ],
        distanceFt
      );
    };
    const hoverPoint = markerAt(sectionMarkers.hoverFt);
    const focusPoint = markerAt(sectionMarkers.focusFt);

    return (
      <section className={styles.section}>
        {geometry && (
          <div className={styles.header} aria-label="Map controls">
            <h2 className={styles.title}>Map</h2>
            <div className={styles.controls}>
              <IconButton
                kind="ghost"
                size="sm"
                align="left"
                label={expanded ? 'Shrink map' : 'Expand map'}
                onClick={(event) => {
                  setExpanded(!expanded);
                  onExpandedChange?.(!expanded);
                  if (!expanded) {
                    event.currentTarget
                      .closest('section')
                      ?.scrollIntoView({ block: 'start' });
                  }
                }}
              >
                {expanded ? <Minimize size={16} /> : <Maximize size={16} />}
              </IconButton>
              {armed && (
                <div className={styles.influence}>
                  <Slider
                    id="wellbore-map-drag-influence"
                    labelText="Influence (ft)"
                    min={0}
                    max={MAX_DRAG_INFLUENCE_FT}
                    step={100}
                    value={influenceFt}
                    onChange={({ value }) => setInfluenceFt(Number(value))}
                  />
                  <FloatingTooltip
                    content="How far a dragged point pulls the points around it, in feet along the wellbore. Points close to the one you drag move the most, and the pull fades out at this distance."
                    placement="bottom"
                  >
                    <Information
                      className={styles.influenceInfo}
                      aria-label="Drag influence"
                    />
                  </FloatingTooltip>
                </div>
              )}
              {/* The lock governs dragging only, so it shows while editing is armed.
                The ends come off the plat and are what an operator least wants to
                nudge, so they start locked. */}
              {armed && (
                <IconButton
                  kind="ghost"
                  size="sm"
                  align="left"
                  isSelected={!unlocked}
                  label={
                    unlocked
                      ? 'Lock SHL and BHL locations'
                      : 'Unlock SHL and BHL locations'
                  }
                  onClick={() => setUnlocked((value) => !value)}
                >
                  {unlocked ? <Unlocked size={16} /> : <Locked size={16} />}
                </IconButton>
              )}
              {/* One button: a text button carries no pressed state, so the label
                flips while editing is armed. */}
              <Button
                kind="tertiary"
                size="sm"
                renderIcon={Edit}
                disabled={
                  armed
                    ? geometry.committing
                    : !geometry.editable || geometry.committing
                }
                onClick={armed ? commit : handleArm}
              >
                {armed ? 'Done Editing' : 'Edit Wellbore Geometry'}
              </Button>
            </div>
          </div>
        )}
        {/* INS-12936 narrative copy. Edit mode armed: the geometry is uncommitted, so say how
            to land it. Stacked with the stick and vendor-survey warnings, as the mock stacks
            them, because each names a different thing to act on. */}
        {armed && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            className={styles.banner}
            title="Editing wellbore geometry."
            subtitle="Confirm your edits by clicking Done Editing. Ensure your wellbore geometry has depths before saving and exiting."
          />
        )}
        {armed && geometry?.surveyed && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            className={styles.banner}
            title="You are editing a vendor provided well survey."
            subtitle="These depths and locations came from a filed survey. Be confident in the change before saving."
          />
        )}
        {geometry?.pointsFailed && (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            className={styles.banner}
            title="Failed to load the wellbore points."
            subtitle="The map shows the filed geometry. Reload the sheet to try again before editing."
          />
        )}
        {stick && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            className={styles.banner}
            title="Simple Stick Wellbore Geometry"
            subtitle="This geometry has only SHL and BHL locations. Wellbore geometry is best represented by three or more points."
          />
        )}
        {saveUnavailable && (
          <InlineNotification
            kind="info"
            lowContrast
            className={styles.banner}
            title="Saving a wellbore geometry edit is not available yet."
            subtitle="The edit is still on the map. This environment does not serve the geometry write, so it cannot be committed from here."
            onCloseButtonClick={() => setSaveUnavailable(false)}
          />
        )}
        <div
          className={classNames(styles.mapContainer, {
            [styles.mapContainerExpanded]: expanded,
          })}
        >
          {(loading || geometry?.committing) && (
            <InlineLoading className={styles.refreshing} />
          )}
          <Map
            longitude={center[0]}
            latitude={center[1]}
            zoom={DEFAULT_ZOOM}
            mapConfigAtom={preDrillMapConfigAtom}
            refAtom={preDrillMapRefAtom}
            showControls
            showMapFilters
            className={styles.map}
            scopedLayers={
              <WellScopedLayers
                hasIntersectingUnits={false}
                wellboreSummary={wellboreSummary}
              />
            }
          >
            <>
              {/* Color-by overlay: matched wells painted by attribute color +
                drawn thicker, over the base wells layer. */}
              <PreDrillScopedWellsLayer colorRules={colorRules} />
              {isProducingVisible &&
                producingWellbores.map((wellbore) => (
                  <WellMapLayer
                    key={`producing-${wellbore.uwi14}`}
                    id={`producing-${wellbore.uwi14}`}
                    wellbore={wellbore}
                    mapConfigAtom={preDrillMapConfigAtom}
                    colorOverride={SUBJECT_WELL_COLOR}
                  />
                ))}
              {sidetrackWellbores
                .filter((wellbore) => sidetrackVisibility[wellbore.uwi14])
                .map((wellbore) => (
                  <WellMapLayer
                    key={`sidetrack-${wellbore.uwi14}`}
                    id={`sidetrack-${wellbore.uwi14}`}
                    wellbore={wellbore}
                    mapConfigAtom={preDrillMapConfigAtom}
                    colorOverride={colorFor(wellbore.uwi14)}
                  />
                ))}
              {/* Story 7: the bore as its points define it, over the vendor stick, with
                  its section line. The handles show only while armed.

                  Hidden by the Scoped > Wells > Subject toggle, the same one that gates
                  the producing layer above. It has to be gated HERE too: the subject of
                  an authored well is an LTD composite, classified 'Pre-Drill', so it
                  never reaches `producingWellbores` and this line is the only thing
                  drawing it — leaving the toggle with nothing to act on.

                  ...unless an edit is armed. This Source carries the transparent drag
                  hitbox as well as the visible line, so honouring the toggle mid-edit
                  would leave the reader dragging something they cannot see. Editing
                  wins; the toggle reasserts the moment it ends. */}
              {geometry &&
                shown.length >= 2 &&
                (isProducingVisible || armed) && (
                  <Source
                    id="pre-drill-working-geometry"
                    type="geojson"
                    data={workingLine}
                  >
                    <Layer
                      id="pre-drill-working-geometry-line"
                      type="line"
                      paint={{
                        'line-color': WORKING_LINE_COLOR,
                        'line-width': WORKING_LINE_WIDTH,
                      }}
                    />
                    <Layer
                      id={WORKING_HITBOX_LAYER_ID}
                      type="line"
                      paint={{
                        'line-color': WORKING_LINE_COLOR,
                        'line-width': WORKING_HITBOX_WIDTH,
                        'line-opacity': 0,
                      }}
                    />
                  </Source>
                )}
              {sectionFeature && (
                <Source
                  id="pre-drill-section-line"
                  type="geojson"
                  data={sectionFeature}
                >
                  <Layer
                    id="pre-drill-section-line-line"
                    type="line"
                    paint={{
                      'line-color': SECTION_LINE_COLOR,
                      'line-width': SECTION_LINE_WIDTH,
                    }}
                  />
                </Source>
              )}
              {/* What the section is pointing at, on the line itself: the cursor's
                  place as a white dot, the selected well's crossing as a ring in the
                  same violet the section draws its selection in (UAT 36). */}
              {hoverPoint && (
                <Marker
                  longitude={hoverPoint.lon}
                  latitude={hoverPoint.lat}
                  anchor="center"
                >
                  <div className={styles.sectionIndicator} aria-hidden="true" />
                </Marker>
              )}
              {focusPoint && (
                <Marker
                  longitude={focusPoint.lon}
                  latitude={focusPoint.lat}
                  anchor="center"
                >
                  <div className={styles.sectionFocus} aria-hidden="true" />
                </Marker>
              )}
              {sectionLine &&
                (['a', 'b'] as const).map((end) => (
                  <Marker
                    key={`section-${end}`}
                    longitude={sectionLine[end][0]}
                    latitude={sectionLine[end][1]}
                    anchor="center"
                    draggable
                    onDrag={(event) => handleSectionDrag(end, event)}
                  >
                    <div
                      className={styles.sectionHandle}
                      title="Drag to move the section line"
                      aria-label={`Section line end ${
                        end === 'a' ? 'A' : "A'"
                      }`}
                    >
                      <span className={styles.sectionLabel}>
                        {end === 'a' ? 'A' : "A'"}
                      </span>
                    </div>
                  </Marker>
                ))}
              {/* The vertices belong to editing (UAT 22): none before it, and in it only
                  the ends, the landing point and what this edit added wear a grip. The
                  stations between stay grabbable, drawn as nothing. */}
              {geometry &&
                armed &&
                shown.map((point, index) => {
                  const draggable = unlocked || !point.locationLocked;
                  const marked =
                    index === 0 ||
                    index === shown.length - 1 ||
                    point.type === 'lp' ||
                    point.added === true;
                  const label =
                    index === 0
                      ? 'SHL'
                      : index === shown.length - 1
                        ? 'BHL'
                        : point.type === 'lp'
                          ? 'LP'
                          : `Point ${index}`;
                  return (
                    <Marker
                      key={`point-${index}`}
                      longitude={point.lon}
                      latitude={point.lat}
                      anchor="center"
                      draggable={draggable}
                      onDrag={(event) => handleDrag(index, event)}
                    >
                      <div
                        className={classNames(styles.pointHandle, {
                          [styles.pointHandleShl]: index === 0,
                          [styles.pointHandleBhl]: index === shown.length - 1,
                          // Locked reads as a filled blue grip, as in the mock.
                          [styles.pointHandleLocked]:
                            point.locationLocked && !unlocked,
                          [styles.pointHandleUnmarked]: !marked,
                          [styles.pointHandleDraggable]: draggable,
                        })}
                        title={
                          draggable
                            ? `Drag to move ${label}`
                            : `${label} — position locked; unlock to move`
                        }
                        aria-label={label}
                      />
                    </Marker>
                  );
                })}
            </>
          </Map>
        </div>
      </section>
    );
  }
);
WorkspaceMap.displayName = 'WorkspaceMap';
